import type { ComputedRef, Ref } from 'vue';
import type { BookCanvas } from '@/modules/editor/canvas/BookCanvas.class';
import type { BookCore } from '@/modules/flipcore/BookCore';

interface UseReaderAutoplayOptions {
  getCanvas: () => BookCanvas | undefined;
  getCurrentBook: () => BookCore | null;
  isPlaying: Ref<boolean>;
  isFirstPage: Ref<boolean> | ComputedRef<boolean>;
  isLastPage: Ref<boolean> | ComputedRef<boolean>;
  isStartupCoverAudioCompleted: () => boolean;
  hideControls: () => void;
  showControlsForCurrentViewport: () => void;
}

/**
 * 单次自动播放：从当前页起播，末页播完后停留并恢复交互。
 * 末页再次播放时，先翻回封面，再由书本 onPageChange 续播。
 */
export const useReaderAutoplay = (options: UseReaderAutoplayOptions) => {
  const {
    getCanvas,
    getCurrentBook,
    isPlaying,
    isFirstPage,
    isLastPage,
    isStartupCoverAudioCompleted,
    hideControls,
    showControlsForCurrentViewport
  } = options;

  /** 末页重播时，等待翻回封面后再启动时间轴。 */
  let pendingRestartAutoplay = false;
  let pendingAutoplayAfterCover = false;
  let completedAutoplayPass = false;

  const stopAutoplay = () => {
    pendingRestartAutoplay = false;
    pendingAutoplayAfterCover = false;
    getCanvas()?.pause();
    getCurrentBook()?.setManualFlipBlocked(false);
    isPlaying.value = false;
  };

  /**
   * 启动自动播放：从当前页（双页模式为左侧页）起始时刻开始。
   * 末页播完后由 handleTimelineComplete 翻回首页并继续循环。
   */
  const startAutoplay = () => {
    if (isPlaying.value) return;

    const canvas = getCanvas();
    const book = getCurrentBook();
    if (!canvas || !book) return;

    if (completedAutoplayPass && isLastPage.value) {
      restartAutoplayFromBeginning();
      return;
    }

    if (isFirstPage.value && isStartupCoverAudioCompleted()) {
      startAutoplayAfterCover();
      return;
    }

    completedAutoplayPass = false;
    canvas.cancelBgmFade();
    isPlaying.value = true;
    hideControls();
    // 只锁住手动翻页，保留缩放、声音等其它画布交互。
    book.setManualFlipBlocked(true);
    // 手动上/下页翻页会走手动翻页音效，令共享翻页声 decoder 停留在 playing=true（自然播完不会置回），
    // 若不先 pause 清除，自动播放首个翻页音效（timeline seekPlay）会被 Audio.playAudio 的 playing 守卫拦截丢声。
    canvas.pause();
    canvas.playFromPageIndex(book.currentPageIndex);
  };

  /** 封面启动音频已经自然播完：先翻过封面，到第一页后再启动主时间线。 */
  const startAutoplayAfterCover = () => {
    const canvas = getCanvas();
    const book = getCurrentBook();
    if (!canvas || !book) return;

    completedAutoplayPass = false;
    canvas.cancelBgmFade();
    pendingAutoplayAfterCover = true;
    isPlaying.value = true;
    hideControls();
    book.setManualFlipBlocked(true);
    canvas.pause();
    book.next();
    tryBeginAutoplayAfterRestart();
  };

  const togglePlay = () => {
    if (isPlaying.value) {
      stopAutoplay();
      showControlsForCurrentViewport();
      return;
    }

    startAutoplay();
  };

  /**
   * 从末页翻回首页，到位后继续从头播放（循环播放）。
   * 调用方需已设置 isPlaying 为 true。
   */
  const restartAutoplayFromBeginning = () => {
    const canvas = getCanvas();
    const book = getCurrentBook();
    if (!canvas || !book) {
      isPlaying.value = false;
      return;
    }

    canvas.cancelBgmFade();
    canvas.resumeBgmAfterFade();
    pendingRestartAutoplay = true;
    isPlaying.value = true;
    hideControls();
    book.setManualFlipBlocked(true);
    canvas.pause();
    book.first();
    tryBeginAutoplayAfterRestart();
  };

  /**
   * 翻到首页且翻页动画结束后，将时间轴归零并开始播放。
   * 使用 requestAnimationFrame 等待同一帧内可能接续的下一段翻页动画启动完毕。
   */
  const tryBeginAutoplayAfterRestart = () => {
    if ((!pendingRestartAutoplay && !pendingAutoplayAfterCover) || !getCurrentBook()) return;

    window.requestAnimationFrame(() => {
      const book = getCurrentBook();
      if ((!pendingRestartAutoplay && !pendingAutoplayAfterCover) || !book) return;
      if (!isPlaying.value) return;
      if (book.isAnimating()) return;
      if (pendingRestartAutoplay && !isFirstPage.value) return;
      if (pendingAutoplayAfterCover && isFirstPage.value) return;

      pendingRestartAutoplay = false;
      pendingAutoplayAfterCover = false;
      completedAutoplayPass = false;
      const canvas = getCanvas();
      if (!canvas) {
        isPlaying.value = false;
        return;
      }

      // 合书（book.first 倒放翻页）会走手动翻页音效，令共享翻页声 decoder 停留在 playing=true，
      // 且其自然播放结束时不会自行置回 false。若不先 pause 清除，接下来从头重播时开书到第二页的
      // 翻页音效（timeline seekPlay）会被 Audio.playAudio 的 playing 守卫拦截而丢声。
      canvas.pause();
      canvas.playFromPageIndex(book.currentPageIndex);
    });
  };

  /** timeline 播放到末页完成：停留末页并让 BGM 淡出。 */
  const handleTimelineComplete = () => {
    if (!isPlaying.value) return;

    pendingRestartAutoplay = false;
    pendingAutoplayAfterCover = false;
    completedAutoplayPass = true;
    const canvas = getCanvas();
    canvas?.pause();
    getCurrentBook()?.setManualFlipBlocked(false);
    isPlaying.value = false;
    canvas?.fadeOutBgm(5);
  };

  /** 横竖屏展示模式切换完成后，以共享 UI 状态恢复连续自动播放。 */
  const syncAfterDisplayModeChange = () => {
    if (!isPlaying.value) return;
    if (pendingRestartAutoplay || pendingAutoplayAfterCover) {
      tryBeginAutoplayAfterRestart();
      return;
    }

    const canvas = getCanvas();
    const book = getCurrentBook();
    if (!canvas || !book) return;

    const timeline = canvas.getTimeline();
    if (timeline?.owner === 'main' && timeline.playing) return;

    book.setManualFlipBlocked(true);
    canvas.playFromPageIndex(book.currentPageIndex);
  };

  /** 销毁书本前清除待续播标记。 */
  const cancelPendingRestart = () => {
    pendingRestartAutoplay = false;
    pendingAutoplayAfterCover = false;
    completedAutoplayPass = false;
  };

  return {
    isPlaying,
    togglePlay,
    restartAutoplayFromBeginning,
    stopAutoplay,
    tryBeginAutoplayAfterRestart,
    handleTimelineComplete,
    syncAfterDisplayModeChange,
    cancelPendingRestart
  };
};
